import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Support as SupportIcon } from '@mui/icons-material';

const TICKET_CATEGORIES = [
  { value: 'billing', label: 'Billing' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'account', label: 'Account Issue' },
  { value: 'other', label: 'Other' },
];

export default function SupportTicketModal({ open, onClose, onSubmit, loading = false }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('other');
  const [errors, setErrors] = useState({});

  const handleSubjectChange = (event) => {
    setSubject(event.target.value);
    setErrors({ ...errors, subject: '' });
  };

  const handleMessageChange = (event) => {
    setMessage(event.target.value);
    setErrors({ ...errors, message: '' });
  };

  const handleCategoryChange = (event) => {
    setCategory(event.target.value);
  };

  const handleSubmit = () => {
    // Validation
    const newErrors = {};
    
    if (!subject.trim()) {
      newErrors.subject = 'Please enter a subject';
    }
    
    if (!message.trim()) {
      newErrors.message = 'Please enter a message';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Submit
    onSubmit(subject, message, category);
  };

  const handleClose = () => {
    if (!loading) {
      setSubject('');
      setMessage('');
      setCategory('other');
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SupportIcon color="primary" />
          <Typography variant="h6" component="span">
            Submit Support Ticket
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          We're here to help! Please provide details about your issue and we'll get back to you as soon as possible.
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={category}
            label="Category"
            onChange={handleCategoryChange}
            disabled={loading}
          >
            {TICKET_CATEGORIES.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <TextField
          fullWidth
          label="Subject"
          placeholder="Brief description of your issue..."
          value={subject}
          onChange={handleSubjectChange}
          error={!!errors.subject}
          helperText={errors.subject}
          disabled={loading}
          sx={{ mb: 3 }}
        />
        
        <TextField
          fullWidth
          multiline
          rows={6}
          label="Message"
          placeholder="Please provide as much detail as possible about your issue..."
          value={message}
          onChange={handleMessageChange}
          error={!!errors.message}
          helperText={errors.message || 'Please provide detailed information to help us assist you'}
          disabled={loading}
        />
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !subject.trim() || !message.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : <SupportIcon />}
        >
          {loading ? 'Submitting...' : 'Submit Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

